import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import baseQuery from "../../utils/baseQuery";
import { Einheit } from "../../utils/Einheit";
import { Mengenangabe } from "../../utils/Mengenangabe";

export interface Liste {
    id: number,
    datum: string,
}

export interface ListeMitEintraegen extends Liste {
    gerichte: ListenGericht[],
}
export interface ListenGericht {
    id: number,
    menge: number,
    gericht: {
        id: number,
        name: string,
    }
}

export interface EinkaufslistenEintrag {
    zutatId: number,
    name: string,
    menge: Mengenangabe[],
    kategorie: number,
    istNochDa: boolean | null,
    istEingekauft: boolean,
    kommentar: string | null,
    zusaetzlicheMenge: Mengenangabe | null,
    gerichte: {
        gerichtId: number,
        gerichtName: string,
        anzahlGerichte: number,
        mengeProGericht: number,
        mengeGesamt: number,
        einheit: Einheit,
    }[]
}

export const listenApi = createApi({
    reducerPath: "listen",
    baseQuery: baseQuery,
    tagTypes: ["liste", "listen"],
    endpoints: builder => ({
        listListen: builder.query<Liste[], void>({
            query: () => "listen/",
            providesTags: () => ["listen"]
        }),
        getListe: builder.query<ListeMitEintraegen, number>({
            query: id => `listen/${id}`,
            providesTags: result => result ? [{ type: "liste", id: result.id }] : []
        }),
        addListe: builder.mutation<Liste, void>({
            query: () => ({
                url: "listen/",
                method: "POST",
            }),
            invalidatesTags: () => ["listen"],
        }),
        removeListe: builder.mutation<void, number>({
            query: id => ({
                url: `listen/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, id) => ["listen", { type: "liste", id }],
        }),
        addListenGericht: builder.mutation<void, { listeId: number, gerichtId: number, menge?: number }>({
            query: ({ listeId, gerichtId, menge }) => ({
                url: `listen/${listeId}/gerichte/${gerichtId}`,
                params: { menge },
                method: "POST",
            }),
            invalidatesTags: (result, error, { listeId }) => [{ type: "liste", id: listeId }]
        }),
        removeListenGericht: builder.mutation<void, { listeId: number, gerichtId: number }>({
            query: ({ listeId, gerichtId }) => ({
                url: `listen/${listeId}/gerichte/${gerichtId}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, { listeId }) => [{ type: "liste", id: listeId }],
        }),
        getEinkaeufe: builder.query<EinkaufslistenEintrag[], number>({
            query: (id) => `listen/${id}/einkaeufe`,
            providesTags: (result, _, id) => result ? [{ type: "liste", id }] : []
        }),
        setIstNochDa: builder.mutation<void, { listeId: number, zutatId: number, value: boolean | null }>({
            query: ({ listeId, zutatId, value }) => ({
                url: `listen/${listeId}/einkaeufe/${zutatId}/istNochDa`,
                headers: { "Content-Type": "application/json" },
                body: value,
                method: "POST",
            }),
            async onQueryStarted({ listeId, zutatId, value }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(listenApi.util.updateQueryData("getEinkaeufe", listeId, draft => {
                    const entry = draft.find(x => x.zutatId === zutatId);
                    if (entry)
                        entry.istNochDa = value;
                }));
                queryFulfilled.catch(() => patchResult.undo());
            },
            invalidatesTags: (result, error, { listeId }) => [{ type: "liste", id: listeId }]
        }),
        setIstEingekauft: builder.mutation<void, { listeId: number, zutatId: number, value: boolean }>({
            query: ({ listeId, zutatId, value }) => ({
                url: `listen/${listeId}/einkaeufe/${zutatId}/istEingekauft`,
                headers: { "Content-Type": "application/json" },
                body: value,
                method: "POST",
            }),
            async onQueryStarted({ listeId, zutatId, value }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(listenApi.util.updateQueryData("getEinkaeufe", listeId, draft => {
                    const entry = draft.find(x => x.zutatId === zutatId);
                    if (entry)
                        entry.istEingekauft = value;
                }));
                queryFulfilled.catch(() => patchResult.undo());
            },
            invalidatesTags: (result, error, { listeId }) => [{ type: "liste", id: listeId }]
        }),
        setKommentar: builder.mutation<void, { listeId: number, zutatId: number, value: string }>({
            query: ({ listeId, zutatId, value }) => ({
                url: `listen/${listeId}/einkaeufe/${zutatId}/kommentar`,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(value),
                method: "POST",
            }),
            async onQueryStarted({ listeId, zutatId, value }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(listenApi.util.updateQueryData("getEinkaeufe", listeId, draft => {
                    const entry = draft.find(x => x.zutatId === zutatId);
                    if (entry)
                        entry.kommentar = value;
                }));
                queryFulfilled.catch(() => patchResult.undo());
            },
            invalidatesTags: (result, error, { listeId }) => [{ type: "liste", id: listeId }]
        }),
        setZusaetzlich: builder.mutation<void, { listeId: number, zutatId: number, menge: number, einheit: Einheit }>({
            query: ({ listeId, zutatId, menge, einheit }) => ({
                url: `listen/${listeId}/einkaeufe/${zutatId}/zusaetzlich`,
                params: { menge, einheit },
                method: "POST",
            }),
            invalidatesTags: (result, error, { listeId }) => [{ type: "liste", id: listeId }]
        }),
        removeZusaetzlich: builder.mutation<void, { listeId: number, zutatId: number }>({
            query: ({ listeId, zutatId }) => ({
                url: `listen/${listeId}/einkaeufe/${zutatId}/zusaetzlich`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, { listeId }) => [{ type: "liste", id: listeId }]
        })
    })
});
export const {
    useListListenQuery,
    useGetListeQuery,
    useAddListeMutation,
    useRemoveListeMutation,
    useAddListenGerichtMutation,
    useRemoveListenGerichtMutation,
    useGetEinkaeufeQuery,
    useSetIstNochDaMutation,
    useSetIstEingekauftMutation,
    useSetKommentarMutation,
    useSetZusaetzlichMutation,
    useRemoveZusaetzlichMutation,
} = listenApi;
